import React, { createContext, useContext, useEffect, useState, useCallback } from 'react'
import { User, onAuthStateChanged, signInWithEmailAndPassword, createUserWithEmailAndPassword, signOut, signInAnonymously } from 'firebase/auth'
import { doc, getDoc, setDoc, updateDoc } from 'firebase/firestore'
import { firebaseApp, auth, db } from '../config/firebaseConfig'
import { courses } from '../data/courses'
import { subscribeToNewsletter } from '../utils/newsletter'
import i18n from '../localization/i18n'

// Helper function to create user document ID using Firebase Auth UID
const createUserDocId = (uid: string): string => {
  return uid
}
const validateAmazonOrderId = (orderId: string): boolean => {
  // Amazon order ID format: 3digits-7digits-7digits
  const amazonOrderPattern = /^\d{3}-\d{7}-\d{7}$/
  return amazonOrderPattern.test(orderId)
}

interface CourseProgress {
  currentChapter?: number
  chapterPositions?: { [chapterIndex: number]: number } // Optional - stored in localStorage for performance
  watchedChapters?: { [chapterIndex: number]: boolean }
  completionModalShown?: boolean
  thirdChapterModalShown?: boolean
}

interface UserProfile {
  courses: Array<{courseName: string, orderId: string}>
  displayName?: string
  email: string
  avatar?: string
  agreeTerms: boolean
  agreeMaterials: boolean
  agreePromoMaterials?: boolean
  progress?: {
    [courseId: string]: CourseProgress
  }
  // Add other profile fields as needed
}

interface AuthContextType {
  user: User | null
  userProfile: UserProfile | null
  login: (email: string, password: string) => Promise<void>
  register: (
    email: string,
    password: string,
    displayName?: string,
    agreeTerms?: boolean,
    agreeMaterials?: boolean,
    agreePromoMaterials?: boolean
  ) => Promise<void>
  signInAsGuest: () => Promise<void>
  logout: () => Promise<void>
  loading: boolean
  hasCourseAccess: (courseId: string) => boolean
  addOrderId: (courseName: string, orderId: string) => Promise<boolean>
  refreshUserProfile: () => Promise<void>
  saveCourseProgress: (courseId: string, progress: CourseProgress) => Promise<void>
  loadCourseProgress: (courseId: string) => Promise<CourseProgress | null>
  isAnonymous: boolean
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [ user, setUser ] = useState<User | null>(null)
  const [ userProfile, setUserProfile ] = useState<UserProfile | null>(null)
  const [ loading, setLoading ] = useState(true)

  // Load user profile from Firestore
  const loadUserProfile = async (user: User) => {
    try {
      const userDocId = createUserDocId(user.uid)
      const userDoc = await getDoc(doc(db, 'users', userDocId))
      
      // If profile doesn't exist under UID, check old email-based ID for migration (only for email auth users)
      if (!userDoc.exists() && user.email) {
        const oldUserDocId = user.email.replace('@', '_at_').replace(/\./g, '_dot_')
        const oldUserDoc = await getDoc(doc(db, 'users', oldUserDocId))
        
        if (oldUserDoc.exists()) {
          // Migrate old profile to new UID-based document
          const oldData = oldUserDoc.data()
          const profile: UserProfile = {
            courses: oldData?.courses || [],
            displayName: oldData?.displayName,
            email: oldData?.email || user.email,
            avatar: oldData?.avatar,
            agreeTerms: oldData?.agreeTerms || false,
            agreeMaterials: oldData?.agreeMaterials || false,
            agreePromoMaterials: oldData?.agreePromoMaterials || false,
            progress: oldData?.progress
          }
          
          // Create new document with UID
          await setDoc(doc(db, 'users', userDocId), profile)
          // Note: Consider deleting old document after confirming migration works
          // await deleteDoc(doc(db, 'users', oldUserDocId))
          
          setUserProfile(profile)
          return
        }
      }
      
      if (userDoc.exists()) {
        const data = userDoc.data()
        // Ensure new fields are present, add if missing
        const profile: UserProfile = {
          courses: data?.courses || [],
          displayName: data?.displayName,
          email: data?.email || user.email || 'anonymous',
          avatar: data?.avatar,
          agreeTerms: data?.agreeTerms || false,
          agreeMaterials: data?.agreeMaterials || false,
          agreePromoMaterials: data?.agreePromoMaterials || false,
          progress: data?.progress
        }
        // Update document if fields were missing
        if (!data?.email) {
          await setDoc(doc(db, 'users', userDocId), profile, { merge: true })
        }
        setUserProfile(profile)
      } else {
        // Create default profile (for new anonymous users)
        const defaultProfile: UserProfile = { 
          courses: [], 
          email: user.email || 'anonymous',
          agreeTerms: false,
          agreeMaterials: false,
          agreePromoMaterials: false
        }
        await setDoc(doc(db, 'users', userDocId), defaultProfile)
        setUserProfile(defaultProfile)
      }
    } catch (error) {
      console.error('Error loading user profile:', error)
      setUserProfile(null)
    }
  }

  // Refresh user profile
  const refreshUserProfile = async () => {
    if (user) {
      await loadUserProfile(user)
    }
  }

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      try {
        setUser(user)
        if (user) {
          await loadUserProfile(user)
        } else {
          setUserProfile(null)
        }
      } finally {
        setLoading(false)
      }
    })
    return unsubscribe
  }, [])

  const login = async (email: string, password: string) => {
    setLoading(true)
    try {
      await signInWithEmailAndPassword(auth, email, password)
    } finally {
      // Loading will be set to false by onAuthStateChanged listener
    }
  }

  const register = async (
    email: string,
    password: string,
    displayName?: string,
    agreeTerms = false,
    agreeMaterials = false,
    agreePromoMaterials = false
  ) => {
    setLoading(true)
    try {
      const userCredential = await createUserWithEmailAndPassword(auth, email, password)
      
      const userProfile: UserProfile = { 
        courses: [],
        email: userCredential.user.email || email,
        agreeTerms,
        agreeMaterials,
        agreePromoMaterials,
        ...(displayName && { displayName })
      }
      
      const userDocId = createUserDocId(userCredential.user.uid)
      await setDoc(doc(db, 'users', userDocId), userProfile)
      setUserProfile(userProfile)

      if (agreePromoMaterials) {
        try {
          await subscribeToNewsletter({
            email: userCredential.user.email || email,
            language: i18n.resolvedLanguage || i18n.language || 'en-GB',
            source: 'auth-register',
          })
        } catch (error) {
          console.error('Could not add user to newsletter:', error)
        }
      }
    } finally {
      // Loading will be set to false by onAuthStateChanged listener
    }
  }

  const signInAsGuest = async () => {
    setLoading(true)
    try {
      await signInAnonymously(auth)
    } finally {
      // Loading will be set to false by onAuthStateChanged listener
    }
  }

  const logout = async () => {
    await signOut(auth)
    setUserProfile(null)
  }

  // Check if user has access to a specific course
  const hasCourseAccess = (courseId: string): boolean => {
    if (!userProfile?.courses?.length) return false
    return userProfile.courses.some(course => course.courseName === courseId)
  }

  // Add a new order ID to user's profile
  const addOrderId = async (courseName: string, orderId: string): Promise<boolean> => {
    if (!user || !validateAmazonOrderId(orderId)) {
      return false
    }

    try {
      const userDocId = createUserDocId(user.uid)
      const currentCourses = userProfile?.courses || []
      
      // Check if this course is already in the user's courses
      if (currentCourses.some(course => course.courseName === courseName)) {
        // Course already exists - this is OK as user might have multiple products
        return true
      }
      
      // Add the new course entry
      const updatedCourses = [...currentCourses, { courseName, orderId }]
      await setDoc(doc(db, 'users', userDocId), {
        courses: updatedCourses
      }, { merge: true })
      
      setUserProfile(prev => prev ? { ...prev, courses: updatedCourses } : null)
      return true
    } catch (error) {
      console.error('Error adding order ID:', error)
      return false
    }
  }

  // Save course progress to Firestore
  const saveCourseProgress = useCallback(async (courseId: string, progress: CourseProgress) => {
    if (!user) return

    try {
      const userDocId = createUserDocId(user.uid)
      
      // Load current progress to merge with new progress (avoid overwriting fields we're not updating)
      const userDoc = await getDoc(doc(db, 'users', userDocId))
      const currentProgress = userDoc.data()?.progress?.[courseId] || {}
      
      // Merge new progress with existing progress
      const mergedProgress = {
        ...currentProgress,
        ...progress
      }
      
      // Update the specific course progress in Firestore
      await updateDoc(doc(db, 'users', userDocId), {
        [`progress.${courseId}`]: mergedProgress
      })
      
      // Update local state
      setUserProfile(prev => prev ? { 
        ...prev, 
        progress: { 
          ...prev.progress, 
          [courseId]: mergedProgress 
        } 
      } : null)
    } catch (error) {
      console.error('Error saving course progress:', error)
    }
  }, [user, db])

  // Load course progress from Firestore
  const loadCourseProgress = useCallback(async (courseId: string): Promise<CourseProgress | null> => {
    if (!user) return null

    try {
      const userDocId = createUserDocId(user.uid)
      const userDoc = await getDoc(doc(db, 'users', userDocId))
      
      if (userDoc.exists()) {
        const data = userDoc.data()
        return data?.progress?.[courseId] || null
      }
      return null
    } catch (error) {
      console.error('Error loading course progress:', error)
      return null
    }
  }, [user, db])

  const value = {
    user,
    userProfile,
    login,
    register,
    signInAsGuest,
    logout,
    loading,
    hasCourseAccess,
    addOrderId,
    refreshUserProfile,
    saveCourseProgress,
    loadCourseProgress,
    isAnonymous: user?.isAnonymous || false
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
